/**
 * Cliente de sincronizacao que substitui o Firestore.
 *
 * Expoe as MESMAS primitivas que o app ja usava (doc/setDoc/serverTimestamp/
 * onSnapshot), mas por baixo fala com o backend proprio:
 *   - WebSocket  /ws?id=<processId>   -> realtime (estado inicial + updates)
 *   - REST PUT   /api/processes/:id   -> fallback de salvar
 *   - REST GET   /api/health          -> checagem de conexao
 *
 * Assim a logica da calculadora (2000+ linhas) continua praticamente intacta.
 */

// Base da API. Em dev/prod servimos na mesma origem (Vite faz proxy de /api e /ws),
// mas da pra apontar para outro host via VITE_API_URL.
const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

function httpBase(): string {
  return API_BASE || window.location.origin;
}

function wsUrl(id: string): string {
  const base = API_BASE || window.location.origin;
  const url = new URL(base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws";
  url.search = `?id=${encodeURIComponent(id)}`;
  return url.toString();
}

// ---- Tipos minimos compativeis com o codigo existente ---------------------

export interface DocRef {
  id: string;
}

export interface Snapshot {
  exists(): boolean;
  data(): Record<string, unknown> | undefined;
  metadata: { hasPendingWrites: boolean };
}

type NextHandler = (snapshot: Snapshot) => void;
type ErrorHandler = (error: Error) => void;

// Sentinela equivalente a serverTimestamp(): o backend define updatedAt,
// entao removemos este campo antes de enviar.
const SERVER_TIMESTAMP = Symbol("serverTimestamp");

// ---- Shims compativeis com Firestore --------------------------------------

export const db = {} as const;

export function doc(_db: unknown, _collection: string, id: string): DocRef {
  return { id };
}

export function serverTimestamp(): unknown {
  return SERVER_TIMESTAMP;
}

function makeSnapshot(state: Record<string, unknown> | null, pending = false): Snapshot {
  return {
    exists: () => state != null,
    data: () => state ?? undefined,
    metadata: { hasPendingWrites: pending },
  };
}

function stripTimestamps(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === SERVER_TIMESTAMP) continue;
    out[key] = value;
  }
  return out;
}

// ---- Conexao realtime unica (uma por processo aberto) ---------------------

interface Connection {
  id: string;
  socket: WebSocket;
  next: NextHandler;
  error: ErrorHandler;
  closedByUser: boolean;
}

let connection: Connection | null = null;

function openConnection(id: string, next: NextHandler, error: ErrorHandler): Connection {
  const socket = new WebSocket(wsUrl(id));
  const conn: Connection = { id, socket, next, error, closedByUser: false };

  socket.addEventListener("message", (event) => {
    let message: { type?: string; state?: Record<string, unknown> | null; error?: string };
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }

    switch (message.type) {
      // estado inicial OU update vindo de outra pessoa OU confirmacao do meu save
      case "state":
      case "saved":
        next(makeSnapshot(message.state ?? null));
        break;
      case "empty":
        next(makeSnapshot(null));
        break;
      case "error":
        error(new Error(message.error || "sync error"));
        break;
    }
  });

  socket.addEventListener("close", () => {
    if (conn.closedByUser) return;
    // reconecta automaticamente apos uma pequena espera
    setTimeout(() => {
      if (connection === conn) {
        connection = openConnection(id, next, error);
      }
    }, 1500);
  });

  socket.addEventListener("error", () => {
    // o evento "close" cuida da reconexao; aqui so reportamos
  });

  return conn;
}

/**
 * Equivalente ao onSnapshot do Firestore. Abre/usa a conexao WebSocket do
 * processo e dispara `onNext` com o estado inicial e a cada mudanca remota.
 * Retorna a funcao de unsubscribe.
 */
export function onSnapshot(ref: DocRef, onNext: NextHandler, onError: ErrorHandler): () => void {
  if (connection && connection.id !== ref.id) {
    connection.closedByUser = true;
    connection.socket.close();
    connection = null;
  }

  if (!connection || connection.id !== ref.id) {
    connection = openConnection(ref.id, onNext, onError);
  } else {
    connection.next = onNext;
    connection.error = onError;
  }

  const current = connection;
  return () => {
    if (connection === current) {
      current.closedByUser = true;
      current.socket.close();
      connection = null;
    }
  };
}

/**
 * Equivalente ao setDoc. Se ja existe um WebSocket aberto para este processo,
 * envia o save por ele (o servidor avisa os outros). Caso contrario, faz um
 * PUT REST.
 */
export async function setDoc(
  ref: DocRef,
  data: Record<string, unknown>,
  _options?: { merge?: boolean },
): Promise<void> {
  const state = stripTimestamps(data);

  if (connection && connection.id === ref.id && connection.socket.readyState === WebSocket.OPEN) {
    connection.socket.send(JSON.stringify({ type: "save", state }));
    return;
  }

  const response = await fetch(`${httpBase()}/api/processes/${encodeURIComponent(ref.id)}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(state),
  });

  if (!response.ok) {
    throw new Error(`save failed: ${response.status}`);
  }
}

/**
 * Substitui o ensureFirestoreReady: confere se o backend responde.
 */
export async function ensureRemoteReady(_id: string): Promise<void> {
  const response = await fetch(`${httpBase()}/api/health`);
  if (!response.ok) {
    throw new Error("backend indisponivel");
  }
}
